import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import axios from "axios";
import apiUrls from "../../../../api";
import { exportInteractionToCsv } from "../../utils/exportCsv";
import { getSessionDate } from "../../utils/dateUtils";
import { sessionsByProjectGroupSelector } from "../../../stats/state/statsSlice";
import { formatContentTypeName } from "../../../graph/util/NameFormatter";
import { SCENE_CONTENT_TYPES } from "../../constants";
import { shouldHideDuration } from "../../utils/sessionValidation";
import InteractionContentRenderer from "./InteractionContentRenderer";
import GazeHeatmapModal from "./GazeHeatmapModal";
import { useSceneDisplayMode, useSessionGraphStats } from "../../hooks";
import { StatsGraph } from "../charts/StatsGraph";
import type {
  SessionSubdata,
  RawSessionSubdataItem,
  TransformedItem,
  GroupedInteraction,
} from "../../types/sessionTypes";

/** Scene info for heatmap button */
interface SceneInfo {
  id: number;
  name: string;
  contentTypeName: string;
  sceneType?: string; // image360, video360, etc.
}

interface SessionDetailModalProps {
  sessionId: number;
  onClose: () => void;
}

type ViewState = "sessions" | "interaction";

/** Scene group for hierarchical display */
interface SceneGroup {
  sceneId: number | null; // null for orphan interactions
  sceneName: string;
  interactions: GroupedInteraction[];
  totalScore: number;
  totalScoreMax: number;
  interactionCount: number;
}

const SessionDetailModal = ({ sessionId, onClose }: SessionDetailModalProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  const [currentView, setCurrentView] = useState<ViewState>("sessions");
  const [selectedInteraction, setSelectedInteraction] = useState<SessionSubdata | null>(null);
  const [sessionSubdata, setSessionSubdata] = useState<SessionSubdata[]>([]);
  const [allVisitedContentIds, setAllVisitedContentIds] = useState<Set<number>>(new Set());
  const [scenes, setScenes] = useState<SceneInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [interactionLlm, setInteractionLlm] = useState<string | null>(null);
  const fetchedSessionIdRef = useRef<number | null>(null);
  const [expandedContentIds, setExpandedContentIds] = useState<Set<number>>(new Set());
  const [expandedSceneIds, setExpandedSceneIds] = useState<Set<number>>(new Set());
  const [showHeatmapModal, setShowHeatmapModal] = useState(false);
  // Display mode: "detailed" (with scene hierarchy) or "summary" (flat list)
  const [displayMode, setDisplayMode] = useSceneDisplayMode("summary");
  // Search filter for interactions and scenes
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Find the session and projectId in Redux state
  const { session, projectId } = useMemo(() => {
    for (const group of sessionsByProjectGroup) {
      for (const project of group.projects) {
        const found = project.sessions.find((s) => s.id === sessionId);
        if (found) return { session: found, projectId: project.project_id };
      }
    }
    return { session: null, projectId: null };
  }, [sessionsByProjectGroup, sessionId]);

  // Fetch session subdata
  useEffect(() => {
    const fetchSubdata = async () => {
      if (!sessionId) return;

      // Prevent duplicate fetches (React StrictMode)
      if (fetchedSessionIdRef.current === sessionId) return;
      fetchedSessionIdRef.current = sessionId;

      setIsLoading(true);
      try {
        const response = await axios.get(`${apiUrls.stats.sessionSubdatas}?sessionId=${sessionId}`);

        // Handle different Strapi response formats
        let rawData: RawSessionSubdataItem[] = [];
        if (Array.isArray(response.data)) {
          // Direct array response
          rawData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // Strapi v4 format: { data: [...] }
          rawData = response.data.data;
        } else if (response.data?.results && Array.isArray(response.data.results)) {
          // Alternative format: { results: [...] }
          rawData = response.data.results;
        }

        // Transform all items first
        const allItems: TransformedItem[] = rawData.map(
          (item: RawSessionSubdataItem): TransformedItem => {
            // Handle both flat and nested (Strapi v4) formats
            const attrs = item.attributes || item;
            const contentData = attrs.content?.data || attrs.content;

            // Get content type name (from content.content_type.type)
            const contentTypeName =
              contentData?.content_type?.type ||
              contentData?.attributes?.content_type?.data?.attributes?.name ||
              "";

            // Get score_max from content
            const scoreMax = contentData?.attributes?.score_max ?? contentData?.score_max ?? null;

            // Get be_scored from content
            const beScored = contentData?.attributes?.be_scored ?? contentData?.be_scored ?? true;

            // Get parent id and name (the scene that contains this interaction)
            // Handle various Strapi response formats
            const rawParent = contentData?.parent || contentData?.attributes?.parent;
            const parentId =
              // Direct ID (when parent is just a number)
              (typeof rawParent === "number" ? rawParent : null) ||
              // Object with id
              rawParent?.id ||
              // Nested data format
              rawParent?.data?.id ||
              undefined;
            const parentName =
              rawParent?.name || rawParent?.data?.name || rawParent?.attributes?.name || undefined;

            return {
              id: item.id,
              score: attrs.score ?? 0,
              scoreMax,
              beScored,
              time_start_content: attrs.time_start_content ?? 0,
              time_end_content: attrs.time_end_content ?? 0,
              specific_data: attrs.specific_data,
              contentTypeName: contentTypeName.toLowerCase(),
              version: attrs.version,
              content: contentData
                ? {
                    id: contentData.id || contentData.attributes?.id,
                    name: contentData.attributes?.name || contentData.name || "-",
                    type: formatContentTypeName(contentTypeName) || undefined,
                    parentId,
                    parentName,
                  }
                : undefined,
            };
          },
        );

        // Extract scenes for heatmap feature (content_scene only)
        const sceneItems: SceneInfo[] = allItems
          .filter(
            (item): item is typeof item & { content: NonNullable<typeof item.content> } =>
              item.contentTypeName === "content_scene" && !!item.content?.id,
          )
          .map((item) => ({
            id: item.content.id,
            name: item.content.name,
            contentTypeName: item.contentTypeName,
          }))
          // Remove duplicates by id
          .filter((scene, index, self) => self.findIndex((s) => s.id === scene.id) === index);
        setScenes(sceneItems);

        // Fetch scene types from gazeHeatmapData API if we have scenes
        if (sceneItems.length > 0) {
          try {
            const heatmapResponse = await axios.get(
              `${apiUrls.stats.gazeHeatmapData}?sessionId=${sessionId}`,
            );
            const heatmapScenes = heatmapResponse.data?.scenes || [];

            // Update scenes with sceneType from heatmap data
            const scenesWithTypes = sceneItems.map((scene) => {
              const heatmapScene = heatmapScenes.find(
                (hs: { sceneId: number; media?: { sceneType?: string } }) =>
                  hs.sceneId === scene.id,
              );
              return {
                ...scene,
                sceneType: heatmapScene?.media?.sceneType || undefined,
              };
            });
            setScenes(scenesWithTypes);
          } catch (heatmapError) {
            console.warn("Could not fetch scene types:", heatmapError);
            // Keep scenes without sceneType
          }
        }

        // Extract ALL visited content IDs (including scenes and non-scored interactions)
        // This is needed for the graph view to show all visited nodes correctly
        const visitedIds = new Set<number>();
        allItems.forEach((item) => {
          if (item.content?.id && item.time_start_content > 0) {
            visitedIds.add(item.content.id);
            // Also mark parent scene as visited
            if (item.content.parentId) {
              visitedIds.add(item.content.parentId);
            }
          }
        });
        setAllVisitedContentIds(visitedIds);

        // Filter for interactions (excluding scenes, non-scored, etc.)
        const transformedData: SessionSubdata[] = allItems
          .filter((item: TransformedItem) => {
            // Exclude scene types (exact match, not partial - avoid filtering content_scoring_scene, content_hotspot_scene_*, etc.)
            if ((SCENE_CONTENT_TYPES as readonly string[]).includes(item.contentTypeName)) {
              return false;
            }
            // Exclude items with be_scored = false
            if (item.beScored === false) {
              return false;
            }
            // Exclude items with score_max of -1 or 0
            if (item.scoreMax === -1 || item.scoreMax === 0) {
              return false;
            }
            return true;
          })
          // Remove temporary fields (keep scoreMax, rawContentType, and version for display/routing)
          .map(({ contentTypeName, beScored, ...rest }: TransformedItem) => ({
            ...rest,
            rawContentType: contentTypeName,
          })) as SessionSubdata[];

        // Sort by start time
        transformedData.sort((a, b) => a.time_start_content - b.time_start_content);

        setSessionSubdata(transformedData);
      } catch (error) {
        console.error("Error fetching session subdata:", error);
        setSessionSubdata([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubdata();
  }, [sessionId]);

  // Group interactions by content_id, keeping the latest version as main display
  const groupedInteractions = useMemo((): GroupedInteraction[] => {
    const groups: Record<number, SessionSubdata[]> = {};

    // Group by content_id
    sessionSubdata.forEach((subdata) => {
      const contentId = subdata.content?.id;
      if (!contentId) return;

      if (!groups[contentId]) {
        groups[contentId] = [];
      }
      groups[contentId].push(subdata);
    });

    // Convert to GroupedInteraction array
    const result: GroupedInteraction[] = Object.values(groups).map((attempts) => {
      // Sort attempts by version (ascending) - oldest first
      attempts.sort((a, b) => (a.version ?? 0) - (b.version ?? 0));

      // Latest is the last one (highest version)
      const latest = attempts[attempts.length - 1];

      return {
        latest,
        attempts,
        attemptCount: attempts.length,
      };
    });

    // Sort groups by the first attempt's start time (order of first appearance)
    result.sort((a, b) => a.attempts[0].time_start_content - b.attempts[0].time_start_content);

    return result;
  }, [sessionSubdata]);

  // Filter interactions by search query (matches interaction name or scene name)
  const filteredInteractions = useMemo(() => {
    let result = groupedInteractions;

    // Filter by search query (matches interaction name or scene name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((group) => {
        // Check interaction name
        const interactionName = group.latest.content?.name?.toLowerCase() || "";
        if (interactionName.includes(query)) {
          return true;
        }
        // Check scene name (parent) - from visited scenes or from interaction's parentName
        const parentId = group.latest.content?.parentId;
        if (parentId != null) {
          const scene = scenes.find((s) => s.id === parentId);
          const sceneName =
            scene?.name?.toLowerCase() || group.latest.content?.parentName?.toLowerCase() || "";
          if (sceneName.includes(query)) {
            return true;
          }
        }
        return false;
      });
    }

    return result;
  }, [groupedInteractions, searchQuery, scenes]);

  // Group filtered interactions by scene for hierarchical display
  const sceneGroupedInteractions = useMemo((): SceneGroup[] => {
    const sceneMap = new Map<number | null, GroupedInteraction[]>();

    // Group interactions by parentId (scene)
    filteredInteractions.forEach((group) => {
      const parentId = group.latest.content?.parentId ?? null;
      if (!sceneMap.has(parentId)) {
        sceneMap.set(parentId, []);
      }
      sceneMap.get(parentId)?.push(group);
    });

    // Convert to SceneGroup array
    const result: SceneGroup[] = [];

    sceneMap.forEach((interactions, sceneId) => {
      const scene = scenes.find((s) => s.id === sceneId);
      const totalScore = interactions.reduce((sum, g) => sum + g.latest.score, 0);
      const totalScoreMax = interactions.reduce((sum, g) => sum + (g.latest.scoreMax || 0), 0);

      // Use scene name from visited scenes, or parent name from interaction, or fallback
      const sceneName =
        scene?.name ||
        interactions[0]?.latest.content?.parentName ||
        t("statsNew.sessionDetail.unknownScene");

      result.push({
        sceneId,
        sceneName,
        interactions,
        totalScore,
        totalScoreMax,
        interactionCount: interactions.length,
      });
    });

    // Sort: scenes with ID first (by chronological order of first interaction), then orphans
    result.sort((a, b) => {
      if (a.sceneId === null && b.sceneId !== null) return 1;
      if (a.sceneId !== null && b.sceneId === null) return -1;
      // By chronological order of the first interaction's first attempt
      return (
        a.interactions[0].attempts[0].time_start_content -
        b.interactions[0].attempts[0].time_start_content
      );
    });

    return result;
  }, [filteredInteractions, scenes, t]);

  // Fetch graph data for Graph view
  const graphStats = useSessionGraphStats({
    projectId: projectId || 0,
    sessionSubdata,
    groupedInteractions,
    allVisitedContentIds,
  });

  // Filter scenes to only show those with scoring data in the dropdown
  const scenesWithScoringData = useMemo(() => {
    // Get all unique parentIds from interactions
    const parentIdsWithData = new Set<number>();
    groupedInteractions.forEach((group) => {
      const parentId = group.latest.content?.parentId;
      if (parentId != null) {
        parentIdsWithData.add(parentId);
      }
    });

    // Filter scenes to only include those with scoring data
    return scenes.filter((scene) => parentIdsWithData.has(scene.id));
  }, [groupedInteractions, scenes]);

  // Expand all scenes by default when data first loads (not on filter changes)
  const hasInitializedExpandedScenes = useRef(false);
  useEffect(() => {
    // Only initialize once when groupedInteractions first has data
    if (groupedInteractions.length > 0 && !hasInitializedExpandedScenes.current) {
      hasInitializedExpandedScenes.current = true;
      // Get all unique scene IDs from all interactions
      const allSceneKeys = new Set<number>();
      groupedInteractions.forEach((group) => {
        const parentId = group.latest.content?.parentId;
        if (parentId != null) {
          allSceneKeys.add(parentId);
        } else {
          allSceneKeys.add(-1); // For orphan interactions
        }
      });
      setExpandedSceneIds(allSceneKeys);
    }
  }, [groupedInteractions]);

  // Check if all scenes are expanded
  const allScenesExpanded = useMemo(() => {
    if (sceneGroupedInteractions.length === 0) return false;
    return sceneGroupedInteractions.every((sg) => expandedSceneIds.has(sg.sceneId ?? -1));
  }, [sceneGroupedInteractions, expandedSceneIds]);

  // Expand or collapse all scenes
  const toggleAllScenes = () => {
    if (allScenesExpanded) {
      // Collapse all
      setExpandedSceneIds(new Set());
    } else {
      // Expand all
      const allSceneKeys = new Set(sceneGroupedInteractions.map((sg) => sg.sceneId ?? -1));
      setExpandedSceneIds(allSceneKeys);
    }
  };

  // Use session score directly for consistency with SessionCard
  // Completion is based on how many points the user could have scored from attempted interactions
  // We use groupedInteractions to count unique interactions (not all attempts)
  const computedScores = useMemo(() => {
    // Sum scoreMax from latest attempt of each unique interaction
    const attemptedScoreMax = groupedInteractions.reduce(
      (sum, group) => sum + (group.latest.scoreMax || 0),
      0,
    );
    // Count unique interactions completed (not total attempts)
    const interactionsCompleted = groupedInteractions.length;
    const sessionScoreMax = session?.score_max ?? 0;

    return {
      score: session?.score ?? 0,
      scoreMax: sessionScoreMax,
      attemptedScoreMax, // Points possible from attempted interactions
      interactionsCompleted,
      totalInteractions: sessionScoreMax,
    };
  }, [groupedInteractions, session?.score, session?.score_max]);

  if (!session) {
    return null;
  }

  const getScoreColor = (score: number, maxScore: number): string => {
    if (maxScore === 0) return "text-gray-600";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const handleInteractionClick = (subdata: SessionSubdata) => {
    setSelectedInteraction(subdata);
    setInteractionLlm(null); // Reset LLM for new interaction
    setCurrentView("interaction");
  };

  const handleBackToSessions = () => {
    setCurrentView("sessions");
    setInteractionLlm(null); // Reset LLM when going back
    // Small delay before clearing the interaction to allow animation
    setTimeout(() => setSelectedInteraction(null), 300);
  };

  const formatDurationReadable = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m${secs.toString().padStart(2, "0")}s`;
  };

  const handleExportInteraction = async () => {
    if (!selectedInteraction) return;
    const locale = i18n.language === "fr" ? "fr" : "en";
    await exportInteractionToCsv(selectedInteraction, {
      filename: "interaction",
      locale,
    });
  };

  const toggleExpanded = (contentId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedContentIds((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) {
        next.delete(contentId);
      } else {
        next.add(contentId);
      }
      return next;
    });
  };

  const toggleSceneExpanded = (sceneId: number | null, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedSceneIds((prev) => {
      const next = new Set(prev);
      const key = sceneId ?? -1; // Use -1 for null (orphan interactions)
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div
      className="stats-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-detail-title"
    >
      <div
        className="stats-modal-content w-full max-w-6xl h-[80vh] max-h-[800px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Carousel container */}
        <div
          className="flex transition-transform duration-300 ease-in-out h-full"
          style={{
            transform: currentView === "interaction" ? "translateX(-50%)" : "translateX(0)",
            width: "200%",
          }}
        >
          {/* Panel 1: Sessions list */}
          <div className="w-1/2 flex-shrink-0 flex flex-col h-full">
            {/* Header - sticky with shadow */}
            <div className="sticky top-0 z-10 bg-white p-6 border-b border-gray-200 shadow-sm">
              <div className="flex items-start justify-between">
                <h2 id="session-detail-title" className="text-2xl font-bold text-gray-900">
                  {format(getSessionDate(session), "dd/MM/yyyy - HH:mm", {
                    locale,
                  })}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={t("general.close")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Stats cards row */}
              <div className="flex items-stretch gap-4 mt-6">
                {/* Score - points obtained vs total possible */}
                <div className="flex flex-col items-center px-6 py-3 border-r border-gray-200">
                  <span className="text-xs text-gray-500 mb-1">
                    {t("statsNew.sessionDetail.score")}
                  </span>
                  {isLoading ? (
                    <span className="text-xl font-bold text-gray-400">...</span>
                  ) : computedScores.totalInteractions > 0 ? (
                    <span
                      className={`text-xl font-bold ${getScoreColor(
                        computedScores.score,
                        computedScores.totalInteractions,
                      )}`}
                    >
                      {computedScores.score % 1 === 0
                        ? computedScores.score
                        : computedScores.score.toFixed(1)}
                      /{computedScores.totalInteractions}
                      <span className="text-sm font-normal text-gray-500 ml-1">pts</span>
                    </span>
                  ) : (
                    <span className="text-xl font-bold text-gray-400">-</span>
                  )}
                </div>

                {/* Progression - total session completion percentage */}
                <div className="flex flex-col items-center px-6 py-3 border-r border-gray-200">
                  <span className="text-xs text-gray-500 mb-1">
                    {t("statsNew.sessionDetail.progression")}
                  </span>
                  {session.total_session_completion_percentage != null ? (
                    <span
                      className={`text-xl font-bold ${getScoreColor(
                        session.total_session_completion_percentage,
                        100,
                      )}`}
                    >
                      {Math.round(session.total_session_completion_percentage)}%
                    </span>
                  ) : (
                    <span className="text-xl font-bold text-gray-400">-</span>
                  )}
                </div>

                {/* User */}
                <div className="flex flex-col items-center px-6 py-3 border-r border-gray-200">
                  <span className="text-xs text-gray-500 mb-1">User</span>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                      {session.enduser?.source_avatar?.s3_url ? (
                        <img
                          src={session.enduser.source_avatar.s3_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {session.user_name ||
                        session.enduser?.username ||
                        t("statsNew.sessionDetail.anonymous")}
                    </span>
                  </div>
                </div>

                {/* Duration - hidden for old sessions with short duration */}
                <div className="flex flex-col items-center px-6 py-3">
                  <span className="text-xs text-gray-500 mb-1">
                    {t("statsNew.sessionDetail.totalDuration")}
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {shouldHideDuration(
                      session.createdAt || session.created_at_ts || 0,
                      session.total_session_duration,
                    )
                      ? "-"
                      : formatDurationReadable(session.total_session_duration || 0)}
                  </span>
                </div>
              </div>

              {/* Search, display mode toggle and heatmap button */}
              {scenes.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left side: Search + Display mode toggle */}
                    <div className="flex items-center gap-4 flex-1">
                      {/* Search bar */}
                      <div className="relative flex-1 max-w-xs">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={t("general.search")}
                          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Display mode toggle (only show if there are scenes with scoring data) */}
                      {scenesWithScoringData.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 flex-shrink-0">
                            {t("statsNew.sessionDetail.displayMode")}
                          </span>
                          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                            <button
                              onClick={() => setDisplayMode("summary")}
                              title={t("statsNew.sessionDetail.summaryTooltip")}
                              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                displayMode === "summary"
                                  ? "bg-green-500 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {t("statsNew.sessionDetail.summary")}
                            </button>
                            <button
                              onClick={() => setDisplayMode("detailed")}
                              title={t("statsNew.sessionDetail.detailedTooltip")}
                              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                                displayMode === "detailed"
                                  ? "bg-green-500 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {t("statsNew.sessionDetail.detailed")}
                            </button>
                            <button
                              onClick={() => setDisplayMode("graph")}
                              title={t("statsNew.graph.title")}
                              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                                displayMode === "graph"
                                  ? "bg-green-500 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {t("statsNew.sessionDetail.graphView")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Heatmap button - right side */}
                    <button
                      onClick={() => setShowHeatmapModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm flex-shrink-0"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>{t("statsNew.gazeHeatmap.viewHeatmap")}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Search bar only (when no scenes but there are interactions) */}
              {scenes.length === 0 && groupedInteractions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="relative max-w-xs">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("general.search")}
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                </div>
              ) : filteredInteractions.length === 0 && displayMode !== "graph" ? (
                <div className="text-center py-8 text-gray-500">
                  {t("statsNew.sessionDetail.noInteractions")}
                </div>
              ) : displayMode === "graph" ? (
                /* Graph View - flex container for dynamic height */
                <div className="flex-1 flex flex-col px-6 pb-4 overflow-hidden min-h-0">
                  <StatsGraph
                    nodes={graphStats.nodes}
                    links={graphStats.links}
                    layout={graphStats.layout}
                    isLoading={graphStats.isLoading}
                    error={graphStats.error}
                    showNotVisited={true}
                    onInteractionClick={(node) => {
                      // Find the interaction by content ID and show its detail
                      const interaction = groupedInteractions.find(
                        (g) => g.latest.content?.id === node.contentId,
                      );
                      if (interaction) {
                        handleInteractionClick(interaction.latest);
                      }
                    }}
                    onSceneClick={(node) => {
                      // Find the scene interaction and show its detail
                      const sceneInteraction = sessionSubdata.find(
                        (s) =>
                          s.content?.id === node.contentId &&
                          s.content?.type &&
                          SCENE_CONTENT_TYPES.some((t) =>
                            s.content?.type?.toLowerCase().includes(t.toLowerCase()),
                          ),
                      );
                      if (sceneInteraction) {
                        handleInteractionClick(sceneInteraction);
                      }
                    }}
                  />
                </div>
              ) : (
                <>
                  {/* Single table with sticky header */}
                  <div className="flex-1 px-6 pb-6 overflow-y-auto">
                    <table className="w-full border-separate border-spacing-0" role="table">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="text-left text-sm text-gray-500">
                          <th className="pb-3 pt-6 font-medium w-10" scope="col">
                            {/* Expand/Collapse all button - only in detailed (hierarchical) view */}
                            {displayMode === "detailed" && sceneGroupedInteractions.length > 0 && (
                              <button
                                onClick={toggleAllScenes}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                aria-label={
                                  allScenesExpanded ? t("general.collapse") : t("general.expand")
                                }
                                title={
                                  allScenesExpanded ? t("general.collapse") : t("general.expand")
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={`h-4 w-4 text-gray-500 transition-transform ${
                                    allScenesExpanded ? "rotate-90" : ""
                                  }`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                  />
                                </svg>
                              </button>
                            )}
                          </th>
                          <th className="pb-3 pt-6 font-medium" scope="col">
                            {t("statsNew.sessionDetail.contentName")}
                          </th>
                          <th className="pb-3 pt-6 font-medium" scope="col">
                            {t("statsNew.sessionDetail.contentType")}
                          </th>
                          <th className="pb-3 pt-6 font-medium" scope="col">
                            {t("statsNew.sessionDetail.playTime")}
                          </th>
                          <th className="pb-3 pt-6 font-medium" scope="col">
                            {t("statsNew.sessionDetail.score")}
                          </th>
                          <th className="pb-3 pt-6 font-medium" scope="col">
                            {t("statsNew.sessionDetail.moreDetails")}
                          </th>
                        </tr>
                        <tr>
                          <th colSpan={6} className="p-0">
                            <div className="border-b border-gray-200" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* In summary mode, show interactions directly (flat list) */}
                        {displayMode === "summary"
                          ? filteredInteractions.map((group) => {
                              const contentId = group.latest.content?.id;
                              const isExpanded = contentId
                                ? expandedContentIds.has(contentId)
                                : false;
                              const hasMultipleAttempts = group.attemptCount > 1;

                              return (
                                <React.Fragment key={group.latest.id}>
                                  {/* Main row (latest attempt) */}
                                  <tr
                                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => handleInteractionClick(group.latest)}
                                  >
                                    <td className="py-4 w-10">
                                      {hasMultipleAttempts && contentId && (
                                        <button
                                          onClick={(e) => toggleExpanded(contentId, e)}
                                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                                          aria-label={
                                            isExpanded ? t("general.collapse") : t("general.expand")
                                          }
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`h-4 w-4 text-gray-500 transition-transform ${
                                              isExpanded ? "rotate-90" : ""
                                            }`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M9 5l7 7-7 7"
                                            />
                                          </svg>
                                        </button>
                                      )}
                                    </td>
                                    <td className="py-4 font-medium text-gray-900">
                                      <div className="flex items-center gap-2">
                                        {group.latest.content?.name || "-"}
                                        {hasMultipleAttempts && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {group.attemptCount}{" "}
                                            {t("statsNew.sessionDetail.attempts")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-4 text-gray-500 italic">
                                      {group.latest.content?.type || "-"}
                                    </td>
                                    <td className="py-4 text-gray-700">
                                      {formatDurationReadable(
                                        group.latest.time_end_content -
                                          group.latest.time_start_content,
                                      )}
                                    </td>
                                    <td className="py-4">
                                      <span
                                        className={`font-semibold ${getScoreColor(
                                          group.latest.score,
                                          group.latest.scoreMax || 1,
                                        )}`}
                                      >
                                        {Math.round(group.latest.score)}/
                                        {group.latest.scoreMax || 1}
                                      </span>
                                    </td>
                                    <td className="py-4">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 text-green-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                                        />
                                      </svg>
                                    </td>
                                  </tr>

                                  {/* Expanded rows (previous attempts) */}
                                  {isExpanded &&
                                    hasMultipleAttempts &&
                                    group.attempts
                                      .slice(0, -1)
                                      .reverse()
                                      .map((attempt, index) => (
                                        <tr
                                          key={attempt.id}
                                          className="border-b border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                                          onClick={() => handleInteractionClick(attempt)}
                                        >
                                          <td className="py-3 w-10"></td>
                                          <td className="py-3 pl-6 text-gray-600 text-sm">
                                            <div className="flex items-center gap-2">
                                              <span className="text-gray-400">└</span>
                                              {t("statsNew.sessionDetail.attempt")}{" "}
                                              {group.attemptCount - 1 - index}
                                            </div>
                                          </td>
                                          <td className="py-3 text-gray-400 italic text-sm">
                                            {attempt.content?.type || "-"}
                                          </td>
                                          <td className="py-3 text-gray-600 text-sm">
                                            {formatDurationReadable(
                                              attempt.time_end_content - attempt.time_start_content,
                                            )}
                                          </td>
                                          <td className="py-3">
                                            <span
                                              className={`text-sm ${getScoreColor(
                                                attempt.score,
                                                attempt.scoreMax || 1,
                                              )}`}
                                            >
                                              {Math.round(attempt.score)}/{attempt.scoreMax || 1}
                                            </span>
                                          </td>
                                          <td className="py-3">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="h-4 w-4 text-gray-400"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                                              />
                                            </svg>
                                          </td>
                                        </tr>
                                      ))}
                                </React.Fragment>
                              );
                            })
                          : /* Detailed mode: show hierarchical Scene → Interactions → Attempts */
                            sceneGroupedInteractions.map((sceneGroup) => {
                              const sceneKey = sceneGroup.sceneId ?? -1;
                              const isSceneExpanded = expandedSceneIds.has(sceneKey);

                              return (
                                <React.Fragment key={`scene-${sceneKey}`}>
                                  {/* Scene row - entire row is clickable */}
                                  <tr
                                    className="bg-gray-100 border-b border-gray-200 hover:bg-gray-200 cursor-pointer transition-colors"
                                    onClick={(e) => toggleSceneExpanded(sceneGroup.sceneId, e)}
                                  >
                                    <td className="py-3 w-10">
                                      <div className="p-1">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className={`h-4 w-4 text-gray-600 transition-transform ${
                                            isSceneExpanded ? "rotate-90" : ""
                                          }`}
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      </div>
                                    </td>
                                    <td colSpan={3} className="py-3 font-semibold text-gray-800">
                                      {sceneGroup.sceneName}
                                      <span className="ml-2 text-sm font-normal text-gray-500">
                                        (
                                        {t("statsNew.sessionDetail.interactionsCount", {
                                          count: sceneGroup.interactionCount,
                                        })}
                                        )
                                      </span>
                                    </td>
                                    <td className="py-3">
                                      <span
                                        className={`font-semibold ${getScoreColor(
                                          sceneGroup.totalScore,
                                          sceneGroup.totalScoreMax || 1,
                                        )}`}
                                      >
                                        {Math.round(sceneGroup.totalScore)}/
                                        {sceneGroup.totalScoreMax}
                                      </span>
                                    </td>
                                    <td className="py-3"></td>
                                  </tr>

                                  {/* Interactions within the scene (if expanded) */}
                                  {isSceneExpanded &&
                                    sceneGroup.interactions.map((group) => {
                                      const contentId = group.latest.content?.id;
                                      const isExpanded = contentId
                                        ? expandedContentIds.has(contentId)
                                        : false;
                                      const hasMultipleAttempts = group.attemptCount > 1;

                                      return (
                                        <React.Fragment key={group.latest.id}>
                                          {/* Interaction row (indented) */}
                                          <tr
                                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors bg-white"
                                            onClick={() => handleInteractionClick(group.latest)}
                                          >
                                            <td className="py-4 w-10 pl-4">
                                              {hasMultipleAttempts && contentId && (
                                                <button
                                                  onClick={(e) => toggleExpanded(contentId, e)}
                                                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                                                  aria-label={
                                                    isExpanded
                                                      ? t("general.collapse")
                                                      : t("general.expand")
                                                  }
                                                >
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className={`h-4 w-4 text-gray-500 transition-transform ${
                                                      isExpanded ? "rotate-90" : ""
                                                    }`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M9 5l7 7-7 7"
                                                    />
                                                  </svg>
                                                </button>
                                              )}
                                            </td>
                                            <td className="py-4 font-medium text-gray-900 pl-4">
                                              <div className="flex items-center gap-2">
                                                {group.latest.content?.name || "-"}
                                                {hasMultipleAttempts && (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {group.attemptCount}{" "}
                                                    {t("statsNew.sessionDetail.attempts")}
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                            <td className="py-4 text-gray-500 italic">
                                              {group.latest.content?.type || "-"}
                                            </td>
                                            <td className="py-4 text-gray-700">
                                              {formatDurationReadable(
                                                group.latest.time_end_content -
                                                  group.latest.time_start_content,
                                              )}
                                            </td>
                                            <td className="py-4">
                                              <span
                                                className={`font-semibold ${getScoreColor(
                                                  group.latest.score,
                                                  group.latest.scoreMax || 1,
                                                )}`}
                                              >
                                                {Math.round(group.latest.score)}/
                                                {group.latest.scoreMax || 1}
                                              </span>
                                            </td>
                                            <td className="py-4">
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 text-green-500"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                                                />
                                              </svg>
                                            </td>
                                          </tr>

                                          {/* Expanded rows (previous attempts) */}
                                          {isExpanded &&
                                            hasMultipleAttempts &&
                                            group.attempts
                                              .slice(0, -1)
                                              .reverse()
                                              .map((attempt, index) => (
                                                <tr
                                                  key={attempt.id}
                                                  className="border-b border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                                                  onClick={() => handleInteractionClick(attempt)}
                                                >
                                                  <td className="py-3 w-10"></td>
                                                  <td className="py-3 pl-10 text-gray-600 text-sm">
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-gray-400">└</span>
                                                      {t("statsNew.sessionDetail.attempt")}{" "}
                                                      {group.attemptCount - 1 - index}
                                                    </div>
                                                  </td>
                                                  <td className="py-3 text-gray-400 italic text-sm">
                                                    {attempt.content?.type || "-"}
                                                  </td>
                                                  <td className="py-3 text-gray-600 text-sm">
                                                    {formatDurationReadable(
                                                      attempt.time_end_content -
                                                        attempt.time_start_content,
                                                    )}
                                                  </td>
                                                  <td className="py-3">
                                                    <span
                                                      className={`text-sm ${getScoreColor(
                                                        attempt.score,
                                                        attempt.scoreMax || 1,
                                                      )}`}
                                                    >
                                                      {Math.round(attempt.score)}/
                                                      {attempt.scoreMax || 1}
                                                    </span>
                                                  </td>
                                                  <td className="py-3">
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      className="h-4 w-4 text-gray-400"
                                                      fill="none"
                                                      viewBox="0 0 24 24"
                                                      stroke="currentColor"
                                                    >
                                                      <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                                                      />
                                                    </svg>
                                                  </td>
                                                </tr>
                                              ))}
                                        </React.Fragment>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Panel 2: Interaction detail */}
          <div className="w-1/2 flex-shrink-0 flex flex-col h-full">
            {/* Header with back button - sticky */}
            <div className="sticky top-0 z-10 bg-white p-4 border-b border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBackToSessions}
                    className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    aria-label={t("general.back")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedInteraction?.content?.name || t("statsNew.interaction.title")}
                    {selectedInteraction?.content?.type && (
                      <span className="text-gray-400 font-normal">
                        {" "}
                        - {selectedInteraction.content.type}
                      </span>
                    )}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={t("general.close")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Stats cards row - also sticky below header */}
            {selectedInteraction && (
              <div className="sticky top-[57px] z-10 bg-white p-4 border-b border-gray-200 shadow-sm">
                <div className="flex items-stretch gap-3">
                  {/* Score */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interaction.score")}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {Math.round(selectedInteraction.score)}/{selectedInteraction.scoreMax || 1}
                    </span>
                  </div>

                  {/* Duration */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">
                      {t("statsNew.interaction.duration")}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatDurationReadable(
                        selectedInteraction.time_end_content -
                          selectedInteraction.time_start_content,
                      )}
                    </span>
                  </div>

                  {/* User */}
                  <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                    <span className="text-xs text-gray-500 mb-1">User</span>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                        {session?.enduser?.source_avatar?.s3_url ? (
                          <img
                            src={session.enduser.source_avatar.s3_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {session?.user_name ||
                          session?.enduser?.username ||
                          t("statsNew.sessionDetail.anonymous")}
                      </span>
                    </div>
                  </div>

                  {/* LLM - only shown when available */}
                  {interactionLlm && (
                    <div className="flex flex-col items-center justify-center px-5 py-3 border border-gray-200 rounded-lg min-w-[80px]">
                      <span className="text-xs text-gray-500 mb-1">LLM</span>
                      <span className="text-lg font-bold text-gray-900">{interactionLlm}</span>
                    </div>
                  )}

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Export button */}
                  <button
                    onClick={handleExportInteraction}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors self-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    <span className="text-sm font-medium">Export CSV</span>
                  </button>
                </div>
              </div>
            )}

            {/* Interaction content - Dynamic zone */}
            {/* Interaction content - no global scroll, children handle their own scroll */}
            <div className="flex-1 p-6 overflow-hidden">
              {selectedInteraction && (
                <InteractionContentRenderer
                  interaction={selectedInteraction}
                  onLlmLoaded={setInteractionLlm}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gaze Heatmap Modal - opens on first scene */}
      {showHeatmapModal && scenes.length > 0 && (
        <GazeHeatmapModal
          sceneName={scenes[0].name}
          sessionId={sessionId}
          contentId={scenes[0].id}
          scenes={scenes.map((s) => ({
            id: s.id,
            name: s.name,
            sceneType: s.sceneType,
          }))}
          initialSceneId={scenes[0].id}
          onClose={() => setShowHeatmapModal(false)}
        />
      )}
    </div>
  );
};

export default SessionDetailModal;
